import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Stethoscope, Clock, FileText, Pill, CalendarHeart, Download, Loader2, MessageSquare, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { openPdf } from '../lib/downloads';
import { isUserLoggedIn } from '../lib/auth';

const STATUS_BADGE = {
  pending:  { bg: '#FEF3C7', fg: '#A16207', label: 'Awaiting doctor' },
  reviewed: { bg: '#E8F5E9', fg: '#2A5244', label: 'Doctor responded' },
  closed:   { bg: '#E0E7FF', fg: '#3730A3', label: 'Closed' },
};

export default function Consultations() {
  const nav = useNavigate();
  const [refs, setRefs] = useState(null);
  const [loading, setLoading] = useState(false);

const load = useCallback(async () => {
  setLoading(true);
  try {
    const { data } = await api.get('/referrals/me');
    setRefs(data.referrals);
  } catch (e) {
    setRefs([]);
  } finally {
    setLoading(false);
  }
}, []);
useEffect(() => {
  if (!isUserLoggedIn()) return nav('/login');
  load();
}, [load, nav]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14 fade-up">
      <h1 className="font-display text-4xl sm:text-5xl text-[#1A1A1A]" data-testid="consultations-title">My Consultations</h1>
      <p className="text-[#4A4A4A] mt-3 text-lg">Track every intake summary you sent to a doctor and their response.</p>

      {refs !== null && (
        refs.length === 0 ? (
          <div className="mt-10 text-center text-[#7A7A7A]" data-testid="consultations-empty">No consultations yet. Send an intake summary to a doctor from your summary page.</div>
        ) : (
          <div className="grid gap-5 mt-8" data-testid="consultations-list">
            {refs.map((r) => {
              const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
              const resp = r.response;
              return (
                <Card key={r.id} className="p-6 sm:p-7 rounded-3xl border border-[#E8E1D5] bg-white hb-shadow-soft" data-testid={`consultation-card-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-2xl bg-[#1E3F33] grid place-items-center"><Stethoscope className="h-6 w-6 text-white"/></div>
                      <div>
                        <div className="font-display text-2xl text-[#1A1A1A]">{r.doctor_name}</div>
                        <div className="text-sm text-[#7A7A7A] flex items-center gap-1.5"><Clock className="h-3.5 w-3.5"/>Sent {new Date(r.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <Badge className="rounded-full font-semibold border" style={{ background: s.bg, color: s.fg, borderColor: s.fg+'33' }}>{s.label}</Badge>
                  </div>

                  {r.patient_message && (
                    <div className="mt-4 p-3 rounded-2xl bg-[#F4F1EA] text-[15px] text-[#1A1A1A]">
                      <div className="text-xs uppercase tracking-wide text-[#7A7A7A] mb-1">Your message</div>
                      {r.patient_message}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link to={`/report/${r.report_id}`}>
                      <Button variant="outline" size="sm" className="rounded-full border-[#1E3F33] text-[#1E3F33] font-semibold">
                        <FileText className="h-4 w-4 mr-1.5"/>View report
                      </Button>
                    </Link>
                  </div>

                  {resp ? (
                    <div className="mt-5 p-5 rounded-2xl bg-[#E8F5E9]/40 border border-[#2A5244]/20">
                      <div className="flex items-center gap-2 text-[#2A5244] font-semibold">
                        <ShieldCheck className="h-5 w-5"/> Doctor's response
                        {r.responded_at && <span className="text-xs text-[#7A7A7A] font-normal">· {new Date(r.responded_at).toLocaleString()}</span>}
                      </div>
                      <div className="mt-3 text-[16px] text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{resp.text}</div>

                      {resp.prescriptions?.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-2 font-semibold text-[#1E3F33] mb-2"><Pill className="h-4 w-4"/>Prescription</div>
                          <ul className="space-y-1.5">
                            {resp.prescriptions.map((p, i) => (
                              <li key={i} className="text-[15px] text-[#1A1A1A]">
                                <b>{p.medicine}</b> — {p.dosage} · {p.duration}
                                {p.notes ? <span className="text-[#4A4A4A]"> ({p.notes})</span> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {resp.follow_up_date && (
                        <div className="mt-4 flex items-center gap-2 text-[#1E3F33] font-medium">
                          <CalendarHeart className="h-4 w-4"/> Follow-up: {resp.follow_up_date}
                        </div>
                      )}

                      {resp.signed_pdf_url && (
                        <Button onClick={() => openPdf(resp.signed_pdf_url, `signed-prescription-${r.id}.pdf`)} size="sm" className="mt-4 rounded-full bg-[#E07A5F] hover:bg-[#C9674D] text-white">
                          <Download className="h-4 w-4 mr-1.5"/>Download signed prescription
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="mt-5 p-4 rounded-2xl bg-[#FFF8E1] border border-[#A16207]/20 text-[#7A5A00] flex items-center gap-2 text-sm">
                      <MessageSquare className="h-4 w-4"/> Awaiting doctor's response. We'll notify them via portal & WhatsApp.
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
